import { RefObject, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { getApiErrorMessage } from "@shared/utils/getApiErrorMessage";
import { createAudioFileService } from "@modules/audioFiles/services/mutations";
import { AudioTrackFormValue, AudioTracksFormValues, audioTracksSchema } from "@modules/audioFiles/validations";
import { listAudioFilesQueryKey } from "@modules/audioFiles/services/queries/listAudioFiles/query";

import { TrackRowState } from "./types";

export function useCreateAudioTracksModal(modalRef: RefObject<ModalRef>, onComplete?: () => void) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const [isPending, setIsPending] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [rowStatus, setRowStatus] = useState<Record<string, TrackRowState>>({});

  const methods = useForm<AudioTracksFormValues>({
    resolver: zodResolver(audioTracksSchema),
    mode: "onBlur",
    defaultValues: {
      tracks: [{ position: "1" } as AudioTrackFormValue],
    },
  });

  const { control, getValues, setValue } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: "tracks" });

  const handleAddTrack = () => {
    append({ position: String(fields.length + 1) } as AudioTrackFormValue);
  };

  const handleAddTracksFromFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    const firstRowHasFile = !!getValues("tracks.0.file");
    const startIndex = fields.length === 1 && !firstRowHasFile ? 0 : fields.length;

    fileArray.forEach((file, i) => {
      const index = startIndex + i;

      if (index < fields.length) {
        setValue(`tracks.${index}.file`, file);
      } else {
        append({ position: String(index + 1), file } as AudioTrackFormValue);
      }
    });
  };

  const handleRemoveTrack = (index: number) => {
    const field = fields[index];

    setRowStatus((prev) => {
      const next = { ...prev };
      delete next[field.id];
      return next;
    });

    remove(index);
  };

  const handleReset = () => {
    setRowStatus({});
    setCurrentIndex(null);
    setIsPending(false);
  };

  const submitTracks = async (
    data: AudioTracksFormValues,
    options?: { onSuccess?: (...args: any[]) => void }
  ) => {
    setIsPending(true);

    for (let i = 0; i < data.tracks.length; i++) {
      const field = fields[i];

      if (rowStatus[field?.id]?.status === "success") continue;

      setCurrentIndex(i);
      setRowStatus((prev) => ({ ...prev, [field.id]: { status: "sending" } }));

      try {
        await createAudioFileService({
          book: data.book,
          position: data.tracks[i].position,
          file: data.tracks[i].file,
        });

        setRowStatus((prev) => ({ ...prev, [field.id]: { status: "success" } }));
      } catch (error) {
        setRowStatus((prev) => ({
          ...prev,
          [field.id]: { status: "error", error: getApiErrorMessage(error) },
        }));
        setIsPending(false);
        setCurrentIndex(null);
        return;
      }
    }

    setIsPending(false);
    setCurrentIndex(null);

    queryClient.invalidateQueries({
      queryKey: listAudioFilesQueryKey,
      refetchType: "all",
    });

    open?.({
      type: "success",
      message: translate("v1/audiobook_admin.success.create"),
    });

    onComplete?.();
    options?.onSuccess?.();
  };

  return {
    methods,
    fields,
    rowStatus,
    isPending,
    currentIndex,
    handleAddTrack,
    handleAddTracksFromFiles,
    handleRemoveTrack,
    handleReset,
    submitTracks,
  };
}
